import {useAppDispatch, useAppSelector} from "../../hooks/hooks";
import React, {useEffect, useState} from "react";
import moment from "moment/moment";
import MEventApi from "../../api/mevent.api";
import DataGrid from "../../components/grid/dataGrid";
import {Box, CircularProgress, TableCell, Tooltip} from "@mui/material";
import GlobalInput from "../../components/global/input";
import {ReactComponent as FilterIcon} from "../../assets/icons/filter-icon.svg";
import GlobalButton from "../../components/global/button";
import {ReactComponent as RefreshIcon} from "../../assets/icons/refresh-icon.svg";
import FilterContainer from "../../components/layout/filterContainer";
import DialogTemplate from "../../components/dialogs/dialogTemplate";
import {downloadExcelFile} from "../../helpers/file";
import {ReactComponent as ExportIcon} from "../../assets/icons/export-icon.svg";
import styled from "styled-components";
import GlobalDropdown from "../../components/global/dropdown";
import BannerEventDetails from "./bannerEventDetails";
import {colors} from "../../theme";
import ContentContainer from "../../components/layout/contentContainer";
import {setEventCRN} from "../../store/reducers/app";
import {toast} from "react-toastify";

const EventGrid = (props: any) => {
    const dispatch = useAppDispatch();
    const _eventApi = new MEventApi();
    const appState = useAppSelector((state) => state.appState);
    const { eventCRN } = appState;

    const defaultFilterObject = {
        eventStaffs: [],
        statuses: [],
        bookingNumber: "",
        company: "",
        eventTitle: "",
        startDate: moment().format('YYYY-MM-DD'),
        endDate: moment().add(1, 'years').format('YYYY-MM-DD'),
    }

    const [state, setState] = useState({
        events: [],
        filterObject: defaultFilterObject,
        showLoading: false,
        staffs: [],
        openBannerEventDetails: false,
        managedEvent: null,
        clients: null,
        eventStatuses: null,
        itemCategories: null,
        items: null,
        rateCategories: null,
    });

    const [currentPage, setCurrentPage] = useState(0);
    const [exportDialogState, setExportDialogState] = useState({
        open: false,
        startDate: moment().subtract(1, 'years').format('YYYY-MM-DD'),
        endDate: moment().add(1, 'years').format('YYYY-MM-DD'),
    });

    const loadInitialFilterData = async () => {
        setState(prevState => ({ ...prevState, showLoading: true }));
        try {
            const staffs = await _eventApi.GetStaffs();
            const eventStatuses = await _eventApi.GetEventStatuses();

            setState(prevState => ({
                ...prevState,
                staffs: staffs,
                eventStatuses: eventStatuses,
            }));
        } finally {
            setState(prevState => ({ ...prevState, showLoading: false }));
        }
    }
    
    const loadData = async () => {
        setState(prevState => ({ ...prevState, showLoading: true }));
        try {
            const { eventStaffs, statuses, startDate, endDate } = state.filterObject;

            const eventStaffIds = eventStaffs?.map(staff => staff.id) || [];
            const statusIds = statuses?.map(status => status.id) || [];
            
            const events = await _eventApi.GetManagedEvents(eventStaffIds, statusIds, startDate, endDate);
            
            setState(prevState => ({
                ...prevState,
                events: events,
            }));
        } finally {
            setState(prevState => ({ ...prevState, showLoading: false }));
        }
    };

    const handleCachedEventCRN = async (eventCRN: string) => {
        setState(prevState => ({ ...prevState, showLoading: true }));
        try {
            const managedEvent = await _eventApi.GetManagedEventsByEventCrn(eventCRN)
            if (!managedEvent) {
                // saved event crn is banner event, not managed event
                return;
            }

            const staffs = await _eventApi.GetStaffs()
            const clients = await _eventApi.GetClientsByActivity(true)
            const eventStatuses = await _eventApi.GetEventStatuses()
            const itemCategories = await _eventApi.GetItemCategories()
            const items = await _eventApi.GetItems()
            const rateCategories = await _eventApi.GetRateCategories()

            setState(prevState => ({
                ...prevState,
                openBannerEventDetails: true,
                managedEvent,
                staffs,
                clients,
                eventStatuses,
                itemCategories,
                items,
                rateCategories
            }))
        } finally {
            setState(prevState => ({ ...prevState, showLoading: false }));
        }
    }

    useEffect(() => {
        loadInitialFilterData()

        if (eventCRN) {
            handleCachedEventCRN(eventCRN);
        }
    }, [])

    const handleFilterObjChange = (name: string, value: any) => {
        const processedValue = value === '' ? null : value;

        if (name === 'startDate' || name === 'endDate') {
            const nextFilterObj = {
                ...state.filterObject,
                [name]: processedValue
            };

            const { startDate, endDate } = nextFilterObj;

            if (startDate && endDate) {
                if (moment(endDate).isBefore(moment(startDate))) {
                    toast.error('Selected date is past Start Date.');
                    return;
                }
            }
        }
        
        setState(prevState => ({
            ...prevState,
            filterObject: {
                ...prevState.filterObject,
                [name]: processedValue,
            },
        }));
    };

    const handleFilterObjMultiSelectChange = (event) => {
        const {
            target: { name, value },
        } = event;

        setState(prevState => ({
            ...prevState,
            filterObject: {
                ...prevState.filterObject,
                [name]: [...value]
            }
        }));
    };

    const handleFilterObjEventStaffsRemove = (event) => {
        const eventStaffs = state.filterObject.eventStaffs.filter((eventStaff) => eventStaff.id !== event.id);

        setState(prevState => ({
            ...prevState,
            filterObject: {
                ...prevState.filterObject,
                eventStaffs
            }
        }));
    };

    const handleFilterObjEventStatusesRemove = (event) => {
        const statuses = state.filterObject.statuses.filter((status) => status.id !== event.id);

        setState(prevState => ({
            ...prevState,
            filterObject: {
                ...prevState.filterObject,
                statuses
            }
        }));
    };

    const handleClearAllFilters = () => {
        setState(prevState => ({
            ...prevState,
            filterObject: defaultFilterObject,
        }));
    };

    const handleSearch = async () => {
        await loadData();
    };

    const handleExportDialogOpen = () => {
        setExportDialogState(prevState => ({ ...prevState, open: true }));
    };

    const handleExportDialogClose = () => {
        setExportDialogState(prevState => ({ ...prevState, open: false }));
    };

    const handleExportDateChange = (name: string, value: any) => {
        const processedValue = value === '' ? null : value;
        const nextState = {
            ...exportDialogState,
            [name]: processedValue,
        };

        const { startDate, endDate } = nextState;
        if (startDate && endDate && moment(endDate).isBefore(moment(startDate))) {
            toast.error('Selected end date is before start date.');
            return;
        }

        setExportDialogState(nextState);
    };

    const handleExport = async () => {
        setState(prevState => ({ ...prevState, showLoading: true }));
        try {
            const { startDate, endDate } = exportDialogState;
            const reportData = await _eventApi.ExportManagedEvents(startDate, endDate);
            downloadExcelFile(reportData.fileBlob, reportData.fileName);
            handleExportDialogClose();
        } catch (error: any) {
            console.error('Export failed:', error);
            let message = 'Failed to export events data.';
            // error response body is wrapped in an ArrayBuffer due to responseType: 'arraybuffer'
            const errorData = error?.response?.data;
            if (errorData instanceof ArrayBuffer) {
                try {
                    const parsed = JSON.parse(new TextDecoder().decode(errorData));
                    if (parsed?.message) message = parsed.message;
                    else if (parsed?.error) message = parsed.error;
                } catch { /* keep default message */ }
            }
            toast.error(message);
        } finally {
            setState(prevState => ({ ...prevState, showLoading: false }));
        }
    };

    const getGridData = () => {

        const { bookingNumber, company, eventTitle } = state.filterObject;

        let filteredData = [...state.events];

        if (bookingNumber) filteredData = filteredData.filter((d) => d.bookingNumber.toLowerCase().includes(bookingNumber.toLowerCase()));
        if (company) filteredData = filteredData.filter((d) => d.client.company.name.toLowerCase().includes(company.toLowerCase()));
        if (eventTitle) filteredData = filteredData.filter((d) => d.eventTitle.toLowerCase().includes(eventTitle.toLowerCase()));
        
        return filteredData.sort((a, b) => {
            if (a.firstFunctionStartsAt > b.firstFunctionStartsAt) return 1;
            if (a.firstFunctionStartsAt < b.firstFunctionStartsAt) return -1;
        });
    };

    const handleDetailsPageClose = () => {
        dispatch(setEventCRN(null));
        setState({
            ...state,
            openBannerEventDetails: false,
        });
    };

    return (
        <>
            {!state.openBannerEventDetails ? (
                <>
                    <FilterContainer sx={{ flexDirection: 'row', justifyContent: 'space-between', p: '7px' }}>
                        <FlexBox sx={{ flexDirection: 'column' }}>
                            <FlexBox sx={{ flexDirection: 'row', marginBottom: '10px' }}>
                                <GlobalDropdown label="Event Staff"
                                    field={'id'}
                                    placeholder={"Select Staff"}
                                    data={[...state.staffs].sort((a, b) => {
                                        const nameA = `${a.lastName}, ${a.firstName}`;
                                        const nameB = `${b.lastName}, ${b.firstName}`;
                                        return nameA.localeCompare(nameB);
                                    })}
                                    multiSelect
                                    name={'bcitID'}
                                    isSearch
                                    isRenderValue
                                    isRenderData
                                    renderValue={(staffAccount) => {
                                        return `${staffAccount.lastName}, ${staffAccount.firstName}`
                                    }}
                                    renderData={(staffAccount) => {
                                        return `${staffAccount.lastName}, ${staffAccount.firstName}`
                                    }}
                                    dataItemKey={'eventStaffs'}
                                    value={state.filterObject.eventStaffs}
                                    onChange={handleFilterObjMultiSelectChange}
                                    handleRemove={handleFilterObjEventStaffsRemove}
                                    disabled={state.showLoading}
                                />
                                <GlobalDropdown label="Status"
                                    field={'id'}
                                    placeholder={"Select Status"}
                                    data={state.eventStatuses}
                                    name={'name'}
                                    multiSelect
                                    dataItemKey={'statuses'}
                                    value={state.filterObject.statuses}
                                    onChange={handleFilterObjMultiSelectChange}
                                    handleRemove={handleFilterObjEventStatusesRemove}
                                    disabled={state.showLoading}
                                />
                                <GlobalInput label="Filter"
                                     icon={<FilterIcon />}
                                     placeholder="Events#"
                                     name={'bookingNumber'}
                                     value={state.filterObject.bookingNumber}
                                     onChange={handleFilterObjChange}
                                     disabled={state.showLoading}
                                />
                                <GlobalInput
                                    placeholder="Company"
                                    name={'company'}
                                    value={state.filterObject.company}
                                    onChange={handleFilterObjChange}
                                    disabled={state.showLoading}
                                />
                                <GlobalInput
                                    placeholder="Events Title"
                                    name={'eventTitle'}
                                    value={state.filterObject.eventTitle}
                                    onChange={handleFilterObjChange}
                                    disabled={state.showLoading}
                                />
                                <GlobalButton
                                    sx={{ minWidth: '100px', height: '28px' }}
                                    variant="round"
                                    text="clear all"
                                    secondary={'true'}
                                    endIcon={<RefreshIcon />}
                                    onClick={handleClearAllFilters}
                                    disabled={state.showLoading}
                                />
                            </FlexBox>
                            <FlexBox sx={{ flexDirection: 'row' }}>
                                <GlobalInput
                                    label="Start Date"
                                    placeholder="Start Date"
                                    type="date"
                                    name={'startDate'}
                                    value={state.filterObject.startDate}
                                    onChange={handleFilterObjChange}
                                    // sx={{ width: '155px' }}
                                    disabled={state.showLoading}
                                />
                                <GlobalInput
                                    label="End Date"
                                    placeholder="End Date"
                                    type="date"
                                    name={'endDate'}
                                    value={state.filterObject.endDate}
                                    onChange={handleFilterObjChange}
                                    sx={{ width: '155px' }}
                                    disabled={state.showLoading}
                                />
                            </FlexBox>
                        </FlexBox>

                        <FlexBox sx={{ flexDirection: 'column', justifyContent: 'flex-start', width: '10%' }}>
                            <GlobalButton
                                fullWidth
                                sx={{ marginBottom: '5px' }}
                                variant="round"
                                text="search"
                                onClick={handleSearch}
                                disabled={state.showLoading}
                            />
                            <GlobalButton
                                fullWidth
                                sx={{ marginTop: '5px' }}
                                variant="round"
                                text="export"
                                endIcon={<ExportIcon />}
                                onClick={handleExportDialogOpen}
                                disabled={state.showLoading}
                            />
                        </FlexBox>
                    </FilterContainer>
                    {state.showLoading && (
                        <FlexBox
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                zIndex: 10,
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <CircularProgress size={100} />
                        </FlexBox>
                    )}
                    <DataGrid
                        data={getGridData()}
                        columns={eventGridColumns}
                        dataItemKey='id'
                        topHeight={80}
                        page={currentPage}
                        onPageChange={setCurrentPage}
                        activeCell={state.managedEvent ? { id: state.managedEvent.id } : null}
                        renderRows={(data: any, onChange: any, handleInputFocus: any) => {
                            const processStatusLabel = (statusName: any) => {
                                if (statusName === 'Quote') {
                                    return (
                                        <>
                                            <QuoteLabel>{statusName}</QuoteLabel>
                                        </>
                                    )
                                } else if (statusName === 'Tentative') {
                                    return (
                                        <>
                                            <TentativeLabel>{statusName}</TentativeLabel>
                                        </>
                                    )
                                } else if (statusName === 'Confirmed') {
                                    return (
                                        <>
                                            <ConfirmedLabel>{statusName}</ConfirmedLabel>
                                        </>
                                    )
                                } else if (statusName === 'Cancelled') {
                                    return (
                                        <>
                                            <CancelledLabel>{statusName}</CancelledLabel>
                                        </>
                                    )
                                } else if (statusName === 'Invoiced') {
                                    return (
                                        <>
                                            <InvoicedLabel>{statusName}</InvoicedLabel>
                                        </>
                                    )
                                }
                            }
                            
                            const sortLocations = (locations: any) => {
                                return locations.sort((a, b) => {
                                    if (a < b) return -1;
                                    if (a > b) return 1;
                                })
                            }
                            
                            return (
                                <>
                                    <TableCell sx={{ width: '6%' }}>{data.bookingNumber}</TableCell>
                                    <TableCell sx={{ width: '5%' }}>{processStatusLabel(data.eventStatus.name)}</TableCell>
                                    <TableCell sx={{ width: '10%' }}>{moment(data.firstFunctionStartsAt).format('ddd MMM DD, YYYY hh:mm A')}</TableCell>
                                    <TableCell sx={{ width: '10%' }}>{moment(data.lastFunctionEndsAt).format('ddd MMM DD, YYYY hh:mm A')}</TableCell>
                                    <Tooltip
                                        title={data.client.company.name}
                                        arrow
                                        placement="top"
                                        PopperProps={{
                                            modifiers: [
                                                {
                                                    name: 'offset',
                                                    options: {
                                                        offset: [0, -15],
                                                    },
                                                },
                                            ],
                                        }}
                                    >
                                        <TableCell sx={{ maxWidth: '7rem', width: '7%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{data.client.company.name}</TableCell>
                                    </Tooltip>
                                    <Tooltip
                                        title={data.eventTitle}
                                        arrow
                                        placement="top"
                                        PopperProps={{
                                            modifiers: [
                                                {
                                                    name: 'offset',
                                                    options: {
                                                        offset: [0, -15],
                                                    },
                                                },
                                            ],
                                        }}
                                    >
                                        <TableCell sx={{ maxWidth: '8rem', width: '8%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{data.eventTitle}</TableCell>
                                    </Tooltip>
                                    <Tooltip
                                        title={sortLocations(data.locations).join(", ")}
                                        arrow
                                        placement="top"
                                        PopperProps={{
                                            modifiers: [
                                                {
                                                    name: 'offset',
                                                    options: {
                                                        offset: [0, -15],
                                                    },
                                                },
                                            ],
                                        }}
                                    >
                                        <TableCell sx={{ maxWidth: '1rem', width: '2%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sortLocations(data.locations).join(", ")}</TableCell>
                                    </Tooltip>
                                    <Tooltip
                                        title={data.adminComment}
                                        arrow
                                        placement="top"
                                        PopperProps={{
                                            modifiers: [
                                                {
                                                    name: 'offset',
                                                    options: {
                                                        offset: [0, -15],
                                                    },
                                                },
                                            ],
                                        }}
                                    >
                                        <TableCell sx={{ maxWidth: '10rem', width: '10%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{data.adminComment}</TableCell>
                                    </Tooltip>
                                    <TableCell sx={{ width: '8%' }}>{data.staffAccount ? `${data.staffAccount?.lastName}, ${data.staffAccount?.firstName}` : null}</TableCell>
                                </>
                            );
                        }}
                        clickable={true}
                        onRowClick={async (item: any) => {
                            setState(prevState => ({ ...prevState, showLoading: true }));
                            try {
                                const managedEvent = await _eventApi.GetManagedEventsById(item.id)
                                const staffs = await _eventApi.GetStaffs()
                                const clients = await _eventApi.GetClientsByActivity(true)
                                const eventStatuses = await _eventApi.GetEventStatuses()
                                const itemCategories = await _eventApi.GetItemCategories()
                                const items = await _eventApi.GetItems()
                                const rateCategories = await _eventApi.GetRateCategories()
                                
                                dispatch(setEventCRN(managedEvent.eventCRN))
                                setState(prevState => ({
                                    ...prevState,
                                    openBannerEventDetails: true,
                                    managedEvent,
                                    staffs,
                                    clients,
                                    eventStatuses,
                                    itemCategories,
                                    items,
                                    rateCategories
                                }))
                            } finally {
                                setState(prevState => ({ ...prevState, showLoading: false }));
                            }
                        }}
                    />
                </>
            ) : (
                <BannerEventDetails
                    event={state.managedEvent}
                    staffs={state.staffs}
                    clients={state.clients}
                    eventStatuses={state.eventStatuses}
                    itemCategories={state.itemCategories}
                    onClose={handleDetailsPageClose}
                    items={state.items}
                    rateCategories={state.rateCategories}
                />
            )}
            {exportDialogState.open && (
                <DialogTemplate
                    title={"Export Events Data"}
                    onClose={handleExportDialogClose}
                    disableCenteredContent
                    maxWidth={'xs'}
                    renderContent={() => (
                        <>
                            <ExportDialogRow>
                                <GlobalInput
                                    label="Start Date"
                                    placeholder="Start Date"
                                    type="date"
                                    name={'startDate'}
                                    value={exportDialogState.startDate}
                                    onChange={handleExportDateChange}
                                    disabled={state.showLoading}
                                />
                            </ExportDialogRow>
                            <ExportDialogRow>
                                <GlobalInput
                                    label="End Date"
                                    placeholder="End Date"
                                    type="date"
                                    name={'endDate'}
                                    value={exportDialogState.endDate}
                                    onChange={handleExportDateChange}
                                    disabled={state.showLoading}
                                />
                            </ExportDialogRow>
                            <ExportDialogRow sx={{ justifyContent: 'flex-end', marginTop: '20px', gap: '8px' }}>
                                <GlobalButton
                                    variant="round"
                                    text="reset"
                                    secondary={'true'}
                                    endIcon={<RefreshIcon />}
                                    onClick={() => setExportDialogState(prevState => ({
                                        ...prevState,
                                        startDate: moment().subtract(1, 'years').format('YYYY-MM-DD'),
                                        endDate: moment().add(1, 'years').format('YYYY-MM-DD'),
                                    }))}
                                    disabled={state.showLoading}
                                />
                                <GlobalButton
                                    variant="round"
                                    text="download"
                                    endIcon={<ExportIcon />}
                                    onClick={handleExport}
                                    disabled={state.showLoading || !exportDialogState.startDate || !exportDialogState.endDate}
                                />
                            </ExportDialogRow>
                        </>
                    )}
                />
            )}
        </>
    );
}

export default EventGrid;

const FlexBox = styled(Box)`
    && {
        display: flex;

        & > * {
            margin-right: 10px;
        }

        & > :last-child {
            margin-right: 0px;
        }
    }
`;

const ExportDialogRow = styled(Box)`
    && {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        width: 100%;
        margin-top: 10px;
    }
`;

const StatusLabel = styled.div`
    && {
        text-align: center;
        padding: 2px 6px;
    }
`

const QuoteLabel = styled(StatusLabel)`
    && {
        color: ${colors.quote.color};
        background: ${colors.quote.backgroundColor};
        border: 1px solid ${colors.quote.border};
    }
`

const TentativeLabel = styled(StatusLabel)`
    && {
        color: ${colors.tentative.color};
        background: ${colors.tentative.backgroundColor};
        border: 1px solid ${colors.tentative.border};
    }
`

const ConfirmedLabel = styled(StatusLabel)`
    && {
        color: ${colors.confirmed.color};
        background: ${colors.confirmed.backgroundColor};
        border: 1px solid ${colors.confirmed.border};
    }
`

const CancelledLabel = styled(StatusLabel)`
    && {
        color: ${colors.cancelled.color};
        background: ${colors.cancelled.backgroundColor};
        border: 1px solid ${colors.cancelled.border};
    }
`

const InvoicedLabel = styled(StatusLabel)`
    && {
        color: ${colors.invoiced.color};
        background: ${colors.invoiced.backgroundColor};
        border: 1px solid ${colors.invoiced.border};
    }
`

const eventGridColumns = [
    {
        id: 'bookingNumber',
        label: 'Booking #',
    },
    {
        id: 'eventStatus',
        objectId: 'name',
        label: 'Status',
    },
    {
        id: 'firstFunctionStartsAt',
        label: 'Starts At',
    },
    {
        id: 'lastFunctionEndsAt',
        label: 'Ends At',
    },
    {
        id: 'company',
        label: 'Company',
        disabled: true
    },
    {
        id: 'eventTitle',
        label: 'Events Name',
    },
    {
        id: 'location',
        label: 'Location',
        disabled: true
    },
    {
        id: 'adminComment',
        label: 'Admin Notes',
        disabled: true
    },
    {
        id: 'eventStaff',
        label: 'Event Staff',
        disabled: true
    }
];